import React, { useEffect, useState, useCallback } from "react";
import { Loader2, AlertTriangle, Check, Repeat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { EmptyState } from "./fxui";

export default function FxExceptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/fx/exceptions"); setItems(data.exceptions || []); }
    catch { toast.error("Could not load exceptions"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (ex, resolution) => {
    const k = `${ex.order_number}-${ex.key}`;
    try {
      await api.post("/fx/exceptions/resolve", {
        order_number: ex.order_number, key: ex.key, resolution,
        note: notes[k] || "", customer_message: notes[k] || "",
      });
      toast.success("Exception resolved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resolve"); }
  };

  return (
    <div data-testid="fx-exceptions">
      <p className="text-sm text-[#5C5C5C] mb-5">Every open item issue across all orders. Resolve with one tap and add an optional note for the customer.</p>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : items.length === 0 ? <EmptyState>🎉 No open exceptions. Everything's flowing.</EmptyState>
        : (
        <div className="space-y-3">
          {items.map((ex) => {
            const k = `${ex.order_number}-${ex.key}`;
            return (
              <div key={k} className="bg-white border border-[#B23B3B]/20 rounded-2xl p-5" data-testid={`fx-exc-${k}`}>
                <div className="flex flex-wrap items-start gap-3 mb-3">
                  <AlertTriangle size={18} className="text-[#B23B3B] mt-0.5" />
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-semibold text-[#0D530E]">{ex.item}</p>
                    <p className="text-xs text-[#5C5C5C]">{ex.order_number} · {ex.customer_name}</p>
                    <p className="text-sm text-[#B23B3B] mt-1">{ex.reason || ex.pick_status}{ex.note ? ` — ${ex.note}` : ""}</p>
                  </div>
                  {ex.photo && <img src={ex.photo} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                </div>
                <input value={notes[k] || ""} onChange={(e) => setNotes((n) => ({ ...n, [k]: e.target.value }))}
                  placeholder="Optional note for the customer / record…" data-testid={`fx-exc-note-${k}`}
                  className="w-full bg-white border border-black/10 rounded-xl px-3 py-2 text-sm mb-3" />
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => resolve(ex, "found")} data-testid={`fx-exc-found-${k}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#306D29] text-white"><Check size={14} /> Found it</button>
                  <button onClick={() => resolve(ex, "substitute")} data-testid={`fx-exc-sub-${k}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#2563EB] text-white"><Repeat size={14} /> Substituted</button>
                  <button onClick={() => resolve(ex, "removed")} data-testid={`fx-exc-remove-${k}`}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-black/10 text-[#B23B3B]"><Trash2 size={14} /> Remove from order</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
